import { Link, Head } from "@inertiajs/react"
import { ChevronRight, MapPinned, Phone, Search, ShieldCheck, type LucideIcon } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import {
  canonicalHref,
  localePathsFrom,
  publicMetaDescription,
  type AlternateLink,
  type PublicLocale,
} from "@/lib/public-links"

type VerificationStep = {
  title: string
  description: string
  icon: LucideIcon
}

type DestinationPhoto = {
  card_url: string
  alt_text: string
}

type HomeCopy = {
  kicker: string
  title: string
  intro: string
  search: string
  searchAction: string
  pickIsland: string
  rabTagline: string
  openingSoon: string
  coming: string
  verificationTitle: string
  steps: VerificationStep[]
}

export default function Home({
  locale,
  rab_path = null,
  cres_path = null,
  losinj_path = null,
  rab_photo = null,
  cres_photo = null,
  losinj_photo = null,
  alternate_links = [],
}: {
  locale: PublicLocale
  rab_path?: string | null
  cres_path?: string | null
  losinj_path?: string | null
  rab_photo?: DestinationPhoto | null
  cres_photo?: DestinationPhoto | null
  losinj_photo?: DestinationPhoto | null
  alternate_links?: AlternateLink[]
}) {
  const copy: HomeCopy = {
    en: {
      kicker: "A field guide, not a booking site",
      title: "Practical island facts, with honesty about what has been checked.",
      intro:
        "Opening hours, ferry times, and beach rules will show whether they were checked in person, by phone, or from an official source. Starting with the island of Rab.",
      search: "Try \"sandy beach in Lopar\"",
      searchAction: "Search",
      pickIsland: "Pick your island",
      rabTagline: "The first destination field guide is now open",
      openingSoon: "Coming soon",
      coming: "Coming autumn 2026",
      verificationTitle: "How verification will appear",
      steps: [
        { title: "Walked or called", description: "First-hand checks will be identified clearly.", icon: MapPinned },
        { title: "Official sources", description: "Rules and schedules will link to their primary sources.", icon: Phone },
        { title: "Unknown stays unknown", description: "Unchecked facts are labelled instead of presented as fresh.", icon: ShieldCheck },
      ],
    },
    de: {
      kicker: "Ein Reiseführer, keine Buchungsseite",
      title: "Praktische Inselfakten – mit ehrlichen Angaben zur Überprüfung.",
      intro:
        "Öffnungszeiten, Fähren und Strandregeln zeigen, ob sie vor Ort, telefonisch oder anhand einer offiziellen Quelle überprüft wurden. Wir starten mit der Insel Rab.",
      search: "Versuch \"Sandstrand in Lopar\"",
      searchAction: "Suchen",
      pickIsland: "Insel auswählen",
      rabTagline: "Der erste Reiseziel-Führer ist jetzt verfügbar",
      openingSoon: "Demnächst verfügbar",
      coming: "Kommt im Herbst 2026",
      verificationTitle: "So wird die Überprüfung angezeigt",
      steps: [
        { title: "Vor Ort oder per Telefon", description: "Direkte Prüfungen werden eindeutig gekennzeichnet.", icon: MapPinned },
        { title: "Offizielle Quellen", description: "Regeln und Fahrpläne werden mit Primärquellen verknüpft.", icon: Phone },
        { title: "Unbekannt bleibt unbekannt", description: "Nicht überprüfte Fakten werden klar als solche bezeichnet.", icon: ShieldCheck },
      ],
    },
  }[locale]
  const canonical = canonicalHref(alternate_links, locale)
  const description = publicMetaDescription(
    copy.intro,
    locale === "de"
      ? "Entdecke die ersten veröffentlichten Insel- und Reisezielseiten des zweisprachigen Xplore-Croatia-Feldführers."
      : "Explore the first published island and destination pages in the bilingual Xplore Croatia field guide.",
  )
  const rabCardContent = (
    <>
      <img
        src={rab_photo?.card_url ?? "https://images.unsplash.com/photo-1499002238440-d264edd596ec?auto=format&fit=crop&w=1100&q=60"}
        alt={rab_photo?.alt_text ?? "Rab coastline"}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent from-30% to-accent-hover/80" />
      <div className="absolute inset-x-4 bottom-4 flex items-end justify-between gap-3 md:inset-x-[1.125rem]">
        <span>
          <span className="block font-display text-2xl font-semibold text-surface md:text-[1.75rem]">Rab</span>
          <span className="mt-0.5 block text-[0.8rem] leading-snug text-surface/85 md:text-[0.84rem]">
            {rab_path ? copy.rabTagline : copy.openingSoon}
          </span>
        </span>
        {rab_path && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface/95 text-accent-hover transition-transform group-hover:translate-x-0.5 md:h-[2.125rem] md:w-[2.125rem]">
            <ChevronRight className="h-4 w-4" aria-hidden />
          </span>
        )}
      </div>
    </>
  )
  const featuredIslands = [
    { name: "Lošinj", path: losinj_path, photo: losinj_photo },
    { name: "Cres", path: cres_path, photo: cres_photo },
  ]

  return (
    <>
      <Head title="Xplore Croatia">
        <meta
          name="description"
          content={description}
        />
        <meta property="og:title" content="Xplore Croatia" />
        <meta
          property="og:description"
          content={description}
        />
        {canonical && <link rel="canonical" href={canonical} />}
        {alternate_links.map((link) => (
          <link key={link.href_lang} rel="alternate" hrefLang={link.href_lang} href={link.href} />
        ))}
      </Head>
      <PublicShell locale={locale} localePaths={localePathsFrom(alternate_links)}>
        <main>
          <section className="public-container grid gap-8 py-7 md:grid-cols-[1fr_32.5rem] md:items-center md:gap-12 md:py-12">
            <div>
              <p className="public-kicker">{copy.kicker}</p>
              <h1 className="public-hero-title mt-2 md:mt-3">
                {copy.title}
              </h1>
              <p className="public-lede mt-3 max-w-[32.5rem] md:mt-4">
                {copy.intro}
              </p>
              <div className="public-search-box mt-5 max-w-[30rem] md:mt-6">
                <Search className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden />
                <span className="min-w-0 flex-1 text-[0.94rem] text-placeholder">{copy.search}</span>
                <span className="hidden rounded-[0.625rem] bg-accent px-4 py-2 text-[0.84rem] font-semibold text-surface md:inline-flex">
                  {copy.searchAction}
                </span>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1468413253725-0d5181091126?auto=format&fit=crop&w=1100&q=60"
              alt="Adriatic coast"
              className="hidden h-[21.25rem] w-full rounded-[1.125rem] object-cover md:block"
            />
          </section>

          <section className="public-container pb-7 md:pb-9">
            <h2 className="public-section-title">{copy.pickIsland}</h2>
            <div className="mt-3 grid gap-3 md:mt-4 md:grid-cols-[1.6fr_1fr_1fr] md:gap-4">
              {rab_path ? (
                <Link href={rab_path} className="public-card group relative block min-h-[10.625rem] no-underline md:min-h-[13.125rem]">
                  {rabCardContent}
                </Link>
              ) : (
                <div className="public-card relative min-h-[10.625rem] md:min-h-[13.125rem]">
                  {rabCardContent}
                </div>
              )}
              {featuredIslands.map((island) =>
                island.path ? (
                  <Link
                    key={island.name}
                    href={island.path}
                    className="public-card group relative flex min-h-[5.25rem] items-end justify-between gap-3 p-4 no-underline transition-colors hover:border-accent md:min-h-[13.125rem] md:p-5"
                  >
                    {island.photo && (
                      <>
                        <img
                          src={island.photo.card_url}
                          alt={island.photo.alt_text}
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                        <span className="absolute inset-0 bg-gradient-to-b from-transparent from-30% to-accent-hover/80" />
                      </>
                    )}
                    <span className={`relative font-display text-lg font-semibold md:text-[1.375rem] ${island.photo ? "text-surface" : "text-ink-display"}`}>
                      {island.name}
                    </span>
                    <span className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-accent-hover transition-transform group-hover:translate-x-0.5 md:h-[2.125rem] md:w-[2.125rem] ${island.photo ? "bg-surface/95" : "bg-accent-faded"}`}>
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </span>
                  </Link>
                ) : (
                  <div key={island.name} className="public-muted-card flex min-h-[5.25rem] flex-col justify-end p-4 md:min-h-[13.125rem] md:p-5">
                    <div className="font-display text-lg font-semibold text-ink-muted md:text-[1.375rem]">{island.name}</div>
                    <div className="mt-0.5 text-xs text-placeholder">{copy.coming}</div>
                  </div>
                ),
              )}
            </div>
          </section>

          <section className="public-container pb-9">
            <div className="rounded-[1.125rem] bg-accent-hover px-5 py-5 md:px-7 md:py-6">
              <h2 className="public-section-title-inverse">{copy.verificationTitle}</h2>
              <div className="mt-1 grid gap-1 md:grid-cols-3 md:gap-6">
                {copy.steps.map((step) => {
                  const Icon = step.icon

                  return (
                    <div key={step.title} className="flex gap-3 border-t border-surface-tinted/15 py-4 md:border-t-0">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-tinted/10 text-accent">
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <span>
                        <span className="block text-[0.95rem] font-semibold leading-snug text-surface-tinted">{step.title}</span>
                        <span className="mt-1 block text-[0.82rem] leading-relaxed text-surface-tinted/70">{step.description}</span>
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </section>
        </main>
      </PublicShell>
    </>
  )
}
